import type { AgentCard, AgentKind, AgentStatus } from '../types';

/** Runtimes that make an agent an LLM-powered loop rather than a plain automation. */
const AI_RUNTIME = /claude|hermes|cloud/;

/** The one automation engine the board tracks; everything else is somebody's own system. */
const AUTOMATION_RUNTIME = /n8n/;

/** How a runtime tag is coloured. Not a palette name: it says what kind of worker the card is. */
export type RuntimeTone = 'ai' | 'auto' | 'other';

/**
 * Classifies a runtime for colour. Shares AI_RUNTIME with agentType on purpose — two predicates
 * would let a card read "AI agent" while its tag was coloured as an automation.
 */
export const runtimeTone = (runtime: string): RuntimeTone => {
  const value = runtime.toLowerCase();
  if (AI_RUNTIME.test(value)) {
    return 'ai';
  }
  return AUTOMATION_RUNTIME.test(value) ? 'auto' : 'other';
};

/** Health of a card, collapsing the 'run' dot into 'ok'. */
export const cardStatus = (card: Pick<AgentCard, 'dot'>): AgentStatus => {
  if (card.dot === 'crit') {
    return 'crit';
  }
  if (card.dot === 'warn') {
    return 'warn';
  }
  if (card.dot === 'ok' || card.dot === 'run') {
    return 'ok';
  }
  return 'idle';
};

/** AI agent vs automation, or planned for ideas. */
export const agentType = (card: Pick<AgentCard, 'planned' | 'tags' | 'loop'>): AgentKind => {
  if (card.loop) {
    return 'loop';
  }
  if (card.planned) {
    return 'planned';
  }
  return card.tags.some((t) => AI_RUNTIME.test(t.label.toLowerCase())) ? 'ai' : 'auto';
};
